import React, { useState, useEffect, useRef, useCallback, useId } from "react";

/**
 * SherComboBox — acts as both a free-text input and a searchable select.
 *
 * Option shape:  { label: string, value: string|number, group?: string, disabled?: boolean }
 *
 * onSelect(option)         — called when the user picks an option  → { label, value }
 * onInputChange(text)      — called on every keystroke with the raw string
 * onFreeInput(text)        — called on blur/Enter when the text doesn't match any option
 *                            (this is your "save new ledger name" case)
 *
 * All style/layout props are optional; sensible defaults are built in.
 */

// ─── tiny helpers ────────────────────────────────────────────────────────────

const DEFAULT_OPTION_STYLE = {
    padding: "7px 10px",
    cursor: "pointer",
    fontSize: "12px",
    fontFamily: '"Nunito Sans", sans-serif, Roboto, Arial, Helvetica',
    fontWeight: 400,
    lineHeight: "1.4",
    borderRadius: "4px",
    transition: "background 0.1s",
    textAlign: "left",
};

const GROUP_HEADER_STYLE = {
    padding: "6px 10px 3px",
    fontSize: "11px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "#888",
    userSelect: "none",
    pointerEvents: "none",
};

const SELECTED_STYLE = { backgroundColor: "#007BFF", color: "#fff" };
const HOVER_STYLE    = { backgroundColor: "#f0f0f0" };

// ─── SherComboBox ────────────────────────────────────────────────────────────────

const SherComboBox = ({
    // --- data ---
    options = [],               // [{ label, value, group?, disabled? }]
    value = "",                 // controlled: the TEXT currently in the input

    // --- callbacks ---
    onSelect,                   // (option: {label,value}) => void — user picked from list
    onInputChange,              // (text: string) => void — every keystroke
    onFreeInput,                // (text: string) => void — blur/Enter with no match (new ledger)
    onClear,                    // () => void — cross icon clicked to clear
    onKeyDown,                  // passthrough
    creatable = false,          // enable dynamic option creation
    onCreateOption,             // callback when create option is clicked/selected

    // --- behaviour ---
    placeholder = "",
    readOnly = false,
    disabled = false,
    clearable = true,           // show ✕ button when there's a value
    showArrow = true,           // show dropdown chevron
    loading = false,            // show a spinner instead of options
    emptyMessage = "No results found",

    // --- refs / forwarding ---
    comboRef: externalRef,
    id: externalId,

    // ─── INPUT BOX dimensions / style ────────────────────────────────────────
    inputWidth,                 // width of the wrapper div + input
    inputHeight,                // height of the input field
    inputPadding = "0 28px 0 8px", // internal padding (right room for icons)
    inputStyle = {},            // extra inline styles for the <input>
    inputClassName = "",
    className = "",             // on the outer wrapper
    inlineStyles = {},          // legacy alias for inputStyle
    style = {},                 // standard style prop for the outer wrapper

    // ─── DROPDOWN dimensions / style ─────────────────────────────────────────
    dropdownWidth,              // defaults to match input; can be wider, e.g. "300px" or "200%"
    dropdownMinWidth,           // e.g. "250px"
    dropdownMaxHeight = "220px",
    dropdownOffset = 2,         // gap (px) between input bottom and dropdown top
    dropdownStyle = {},         // extra inline styles for the dropdown container
    dropdownPosition = "bottom",// "bottom" | "top" — which side the list opens toward

    // ─── OPTION style ────────────────────────────────────────────────────────
    optionStyle = {},           // extra inline styles for each option row
    selectedOptionStyle = {},   // extra styles for the selected option
    hoverOptionStyle = {},      // extra styles for hovered option

    // ─── misc ─────────────────────────────────────────────────────────────────
    inputType = "text",
    onClick,
    onBlur,
    onFocus,
    filterFn,                   // custom filter: (option, inputText) => boolean
}) => {

    const hasWidthClass = className && /\bw-\S+/.test(className);
    const hasHeightClass = className && /\bh-\S+/.test(className);

    const finalWidth = inputWidth !== undefined ? inputWidth : (hasWidthClass ? undefined : "100%");
    const finalHeight = inputHeight !== undefined ? inputHeight : (hasHeightClass ? undefined : "32px");

    const generatedId = useId();
    const inputId     = externalId || generatedId;

    // internal ref for the <input> element itself
    const inputRef = useRef(null);
    // merge with external comboRef if provided
    const setRef = useCallback(
        (node) => {
            inputRef.current = node;
            if (!externalRef) return;
            if (typeof externalRef === "function") externalRef(node);
            else externalRef.current = node;
        },
        [externalRef]
    );

    const wrapperRef      = useRef(null);
    const dropdownRef     = useRef(null);

    const getInitialText = () => {
        if (value == null || value === "") return "";
        const matched = options.find(
            (o) => (o.label != null && o.label.toString() === value.toString()) ||
                   (o.value != null && o.value.toString() === value.toString())
        );
        return matched ? matched.label : String(value);
    };

    const [isOpen, setIsOpen]             = useState(false);
    const [inputText, setInputText]       = useState(getInitialText);
    const [typedText, setTypedText]       = useState(getInitialText);
    const [hoveredIndex, setHoveredIndex] = useState(-1);
    const hasSelectedRef  = useRef(false);

    // ── sync controlled value ─────────────────────────────────────────────────
    useEffect(() => {
        if (value == null) {
            setInputText("");
            setTypedText("");
            return;
        }
        const matched = options.find(
            (o) => (o.label != null && o.label.toString() === value.toString()) ||
                   (o.value != null && o.value.toString() === value.toString())
        );
        const txt = matched ? matched.label : String(value);
        setInputText(txt);
        setTypedText(txt);
    }, [value, options]);

    // ── expose clearInput on external ref ─────────────────────────────────────
    useEffect(() => {
        if (!externalRef) return;
        const target = typeof externalRef === "object" ? externalRef : null;
        if (target && target.current) {
            target.current.clearInput = () => {
                setInputText("");
                setTypedText("");
                setIsOpen(false);
                hasSelectedRef.current = false;
            };
        }
    });

    const lastSelectedValueRef = useRef(null);

    // ── auto-select when value matches an option (edit mode) ─────────────────
    // NOTE: This effect ONLY syncs the display text when value/options change.
    // It deliberately does NOT call onSelect() — that would cause side-effects
    // (e.g. fetching OB, resetting roundOff) to fire every time an entry is
    // loaded or options are refreshed. onSelect() is called ONLY by the user
    // picking an option via handleSelectOption (mouse or keyboard).
    useEffect(() => {
        if (value == null || value === "" || !options.length) {
            lastSelectedValueRef.current = null;
            return;
        }

        const matched = options.find(
            (o) => (o.label != null && o.label.toString() === value.toString()) ||
                   (o.value != null && o.value.toString() === value.toString())
        );

        if (matched && lastSelectedValueRef.current !== matched.value) {
            setInputText(matched.label);
            setTypedText(matched.label);
            lastSelectedValueRef.current = matched.value;
            // ✅ DO NOT call onSelect(matched) here — programmatic auto-sync
            // must never trigger parent side-effects (OB fetch, roundOff reset, etc.)
        }
    }, [value, options]);


    // ── filtered option list ──────────────────────────────────────────────────
    const filteredOptions = React.useMemo(() => {
        // Sort options by label alphabetically by default
        const sortedOptions = [...options].sort((a, b) => {
            const labelA = String(a?.label || "").toLowerCase();
            const labelB = String(b?.label || "").toLowerCase();
            return labelA.localeCompare(labelB);
        });

        let list = [];
        if (!typedText) {
            list = [...sortedOptions];
        } else {
            const needle = typedText.toLowerCase();
            list = sortedOptions.filter((o) => {
                if (filterFn) return filterFn(o, typedText);
                return (
                    o.label?.toLowerCase().includes(needle) ||
                    o.value?.toString().toLowerCase().includes(needle)
                );
            });
        }
        if (creatable && typedText && !sortedOptions.some(o => o.label && o.label.toLowerCase() === typedText.toLowerCase())) {
            list.push({ label: `+ Create "${typedText}"`, value: "__create__", isCreate: true, typedText });
        }
        return list;
    }, [options, typedText, filterFn, creatable]);

    // flat list (for keyboard nav, skipping group headers)
    const selectableOptions = filteredOptions.filter((o) => !o.disabled);

    // ── grouped rendering ─────────────────────────────────────────────────────
    const hasGroups = filteredOptions.some((o) => o.group);

    const groupedEntries = React.useMemo(() => {
        if (!hasGroups) return [{ group: null, items: filteredOptions }];
        const map = new Map();
        filteredOptions.forEach((o) => {
            const g = o.group ?? "";
            if (!map.has(g)) map.set(g, []);
            map.get(g).push(o);
        });
        return [...map.entries()].map(([group, items]) => ({ group, items }));
    }, [filteredOptions, hasGroups]);

    // ── event handlers ────────────────────────────────────────────────────────

    const findExactMatch = useCallback(
        (text) => {
            if (text == null) return null;
            const trimmed = text.toString().trim();
            if (!trimmed) return null;
            const normalized = trimmed.replace(/\s+/g, " ").toLowerCase();
            return options.find(
                (o) =>
                    !o.disabled &&
                    ((o.label != null && o.label.toString().trim().replace(/\s+/g, " ").toLowerCase() === normalized) ||
                     (o.value != null && o.value.toString().trim().toLowerCase() === normalized))
            );
        },
        [options]
    );

    const handleSelectOption = useCallback(
        (option) => {
            if (option.isCreate) {
                onCreateOption?.(option.typedText);
                setIsOpen(false);
                setHoveredIndex(-1);
                return;
            }
            lastSelectedValueRef.current = option.value;
            hasSelectedRef.current = true;
            setInputText(option.label);
            setTypedText(option.label);
            setIsOpen(false);
            setHoveredIndex(-1);
            onSelect?.({ ...option, label: option.label, value: option.value });
            onInputChange?.(option.label);
        },
        [onSelect, onInputChange, onCreateOption]
    );

    const handleInputChange = useCallback(
        (e) => {
            const text = e.target.value;
            setInputText(text);
            setTypedText(text);
            setIsOpen(text.length > 0 || document.activeElement === inputRef.current);
            setHoveredIndex(-1);
            hasSelectedRef.current = false;
            lastSelectedValueRef.current = null;
            onInputChange?.(text);

            if (text) {
                const matched = findExactMatch(text);
                if (matched) {
                    lastSelectedValueRef.current = matched.value;
                    hasSelectedRef.current = true;
                    onSelect?.({ ...matched, label: matched.label, value: matched.value });
                }
            }
        },
        [findExactMatch, onInputChange, onSelect]
    );

    const handleClear = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            setInputText("");
            setTypedText("");
            setIsOpen(false);
            setHoveredIndex(-1);
            hasSelectedRef.current = false;
            lastSelectedValueRef.current = null;
            onInputChange?.("");
            onFreeInput?.("");
            onClear?.();
            try {
                onSelect?.({ label: "", value: "" });
            } catch (err) {
                console.warn("SherComboBox: onSelect error during clear:", err);
            }
            inputRef.current?.focus();
        },
        [onInputChange, onFreeInput, onSelect, onClear]
    );

    const commitFreeInput = useCallback(() => {
        const trimmed = (inputText ?? "").toString().trim();
        if (!trimmed) return;
        const matched = findExactMatch(trimmed);
        if (matched) {
            if (lastSelectedValueRef.current !== matched.value) {
                handleSelectOption(matched);
            }
        } else {
            lastSelectedValueRef.current = null;
            onFreeInput?.(trimmed);
        }
    }, [inputText, findExactMatch, handleSelectOption, onFreeInput]);

const handleKeyDown = useCallback(
    (e) => {
        if (!isOpen) {
            if (e.key === "ArrowDown" || e.key === "Enter") {
                setIsOpen(true);
            }
            onKeyDown?.(e);
            return;
        }

        switch (e.key) {
            case "ArrowDown": {
                e.preventDefault();
                setHoveredIndex((i) => {
                    const nextIdx = i < selectableOptions.length - 1 ? i + 1 : 0;
                    if (selectableOptions[nextIdx]) {
                        setInputText(selectableOptions[nextIdx].label);
                    }
                    return nextIdx;
                });
                break;
            }
            case "ArrowUp": {
                e.preventDefault();
                setHoveredIndex((i) => {
                    const nextIdx = i > 0 ? i - 1 : selectableOptions.length - 1;
                    if (selectableOptions[nextIdx]) {
                        setInputText(selectableOptions[nextIdx].label);
                    }
                    return nextIdx;
                });
                break;
            }
            case "Enter": {
                e.preventDefault();
                if (hoveredIndex >= 0 && selectableOptions[hoveredIndex]) {
                    e.stopPropagation(); // ✅ prevent parent's handleKeyDown from firing
                    handleSelectOption(selectableOptions[hoveredIndex]);
                } else {
                    commitFreeInput();
                    setIsOpen(false);
                    onKeyDown?.(e); // only bubble Enter to parent for free-input case
                }
                return; // skip the onKeyDown?.() at the bottom
            }
            case "Escape":
                setIsOpen(false);
                setHoveredIndex(-1);
                setInputText(typedText);
                break;
            case "Tab":
                setIsOpen(false);
                commitFreeInput();
                break;
            default:
                break;
        }
        onKeyDown?.(e); // ArrowUp/Down/Escape/Tab/others still bubble
    },
    [isOpen, hoveredIndex, selectableOptions, handleSelectOption, commitFreeInput, onKeyDown, typedText]
);
    // scroll hovered item into view
    useEffect(() => {
        if (hoveredIndex < 0 || !dropdownRef.current) return;
        const el = dropdownRef.current.querySelector(`[data-idx="${hoveredIndex}"]`);
        if (el) el.scrollIntoView({ block: "nearest" });
    }, [hoveredIndex]);

    // close on outside click
    useEffect(() => {
        const handler = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const handleBlur = useCallback(
        (e) => {
            setTimeout(() => {
                commitFreeInput();
                setIsOpen(false);
            }, 120);
            onBlur?.(e);
        },
        [commitFreeInput, onBlur]
    );

    const handleFocus = useCallback(() => {
        if (readOnly || disabled) return;
        setIsOpen(true);
        onFocus?.();
    }, [readOnly, disabled, onFocus]);

    // ── derived dropdown position ─────────────────────────────────────────────

    const dropdownPositionStyle = dropdownPosition === "top"
        ? { bottom: `calc(100% + ${dropdownOffset}px)`, top: "auto" }
        : { top: `calc(100% + ${dropdownOffset}px)`, bottom: "auto" };

    // ── option rendering helpers ──────────────────────────────────────────────

    const renderOption = (option, flatIndex) => {
        const isSelected = option.label === inputText;
        const isHovered  = hoveredIndex === flatIndex;
        const isDisabled = !!option.disabled;
        const isCreate = !!option.isCreate;

        const style = {
            ...DEFAULT_OPTION_STYLE,
            ...optionStyle,
            ...(isSelected ? { ...SELECTED_STYLE, ...selectedOptionStyle } : {}),
            ...(isHovered && !isSelected ? { ...HOVER_STYLE, ...hoverOptionStyle } : {}),
            ...(isDisabled ? { opacity: 0.45, cursor: "not-allowed" } : {}),
            ...(isCreate ? { color: "#1a3abf", fontWeight: 600 } : {}),
        };

        return (
            <div
                key={`${option.value}-${flatIndex}`}
                data-idx={flatIndex}
                style={style}
                onMouseDown={(e) => {
                    e.preventDefault();
                    if (!isDisabled) handleSelectOption(option);
                }}
                onMouseEnter={() => !isDisabled && setHoveredIndex(flatIndex)}
                role="option"
                aria-selected={isSelected}
                aria-disabled={isDisabled}
            >
                {option.label}
            </div>
        );
    };

    // ── render ────────────────────────────────────────────────────────────────

    let flatIdx = 0;

    const showClear = clearable && inputText && !disabled && !readOnly;

    return (
        <div
            ref={wrapperRef}
            className={`combo_box_container ${className}`}
            style={{
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                width: finalWidth,
                height: finalHeight,
                ...style,
            }}
        >
            {/* ── input ── */}
            <input
                ref={setRef}
                id={inputId}
                type={inputType}
                value={inputText}
                placeholder={placeholder}
                readOnly={readOnly}
                disabled={disabled}
                autoComplete="off"
                aria-autocomplete="list"
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                className={`combo_box_input_field ${inputClassName}`}
                style={{
                    boxSizing: "border-box",
                    width: "100%",
                    height: "100%",
                    margin: 0,
                    padding: inputPadding,
                    fontFamily: '"Nunito Sans", sans-serif, Roboto, Arial, Helvetica',
                    fontSize: "12px",
                    outline: "none",
                    cursor: disabled ? "not-allowed" : "text",
                    ...inlineStyles,
                    ...inputStyle,
                }}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onClick={onClick}
            />

            {/* ── icon cluster (clear + arrow) ── */}
            <span
                style={{
                    position: "absolute",
                    right: "4px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    display: "flex",
                    alignItems: "center",
                    gap: "2px",
                    pointerEvents: "none",
                }}
            >
                {showClear && (
                    <button
                        type="button"
                        tabIndex={-1}
                        aria-label="Clear"
                        onMouseDown={handleClear}
                        style={{
                            pointerEvents: "all",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "0 2px",
                            display: "flex",
                            alignItems: "center",
                            color: "#999",
                            fontSize: "14px",
                            lineHeight: 1,
                        }}
                    >
                        ✕
                    </button>
                )}
                {showArrow && (
                    <svg
                        width="12" height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        style={{
                            color: "#999",
                            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "transform 0.15s",
                        }}
                    >
                        <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                )}
            </span>

            {/* ── dropdown ── */}
            {isOpen && (
                <div
                    ref={dropdownRef}
                    role="listbox"
                    style={{
                        position: "absolute",
                        left: 0,
                        ...dropdownPositionStyle,
                        width: dropdownWidth ?? "100%",
                        minWidth: dropdownMinWidth ?? (finalWidth || "100%"),
                        maxHeight: dropdownMaxHeight,
                        overflowY: "auto",
                        backgroundColor: "#fff",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.10), 0 1px 3px rgba(0,0,0,0.06)",
                        zIndex: 9999,
                        padding: "4px",
                        boxSizing: "border-box",
                        ...dropdownStyle,
                    }}
                >
                    {loading ? (
                        <div style={{ padding: "12px 10px", fontSize: "12px", color: "#888", textAlign: "center" }}>
                            Loading…
                        </div>
                    ) : filteredOptions.length === 0 ? (
                        <div style={{ padding: "12px 10px", fontSize: "12px", color: "#888", textAlign: "center" }}>
                            {emptyMessage}
                        </div>
                    ) : (
                        groupedEntries.map(({ group, items }) => (
                            <div key={group ?? "__default__"}>
                                {group && (
                                    <div style={GROUP_HEADER_STYLE}>{group}</div>
                                )}
                                {items.map((option) => {
                                    const el = renderOption(option, flatIdx);
                                    if (!option.disabled) flatIdx++;
                                    return el;
                                })}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default SherComboBox;

/* ─── USAGE EXAMPLES ──────────────────────────────────────────────────────────

// 1. Basic ledger select (existing records)
const ledgerOptions = [
  { label: "Cash Account",    value: "ACC001" },
  { label: "Sales Account",   value: "ACC002" },
  { label: "Purchase Account",value: "ACC003" },
];

// onSelect fires when user PICKS from the list → you get { label, value }
// onFreeInput fires when user TYPES something new and leaves the field
<SherComboBox
  options={ledgerOptions}
  placeholder="Search or add ledger…"
  onSelect={({ label, value }) => {
    console.log("Edit existing:", value, label);
    setLedgerKey(value);
    setLedgerName(label);
  }}
  onFreeInput={(text) => {
    console.log("Save new ledger:", text);
    saveLedger({ name: text });
  }}
/>

// 2. Edit mode — pass controlled value (the key); component shows the label
<SherComboBox
  options={ledgerOptions}
  value={existingLedger.value}          // pass the KEY to auto-select
  onSelect={({ label, value }) => setLedger({ label, value })}
  onFreeInput={(text) => updateName(text)}
/>

// 3. Wider dropdown than input
<SherComboBox
  options={ledgerOptions}
  inputWidth="160px"
  inputHeight="28px"
  dropdownWidth="320px"           // dropdown is wider than the input box
  dropdownMinWidth="320px"
/>

// 4. Grouped options
const groupedOptions = [
  { label: "Cash",    value: "ACC001", group: "Assets" },
  { label: "Bank",    value: "ACC002", group: "Assets" },
  { label: "Sales",   value: "ACC003", group: "Income" },
  { label: "Expense", value: "ACC004", group: "Expense" },
];

// 5. Old [[key, value]] array migration helper:
const fromArray = (arr) => arr.map(([value, label]) => ({ label, value }));

────────────────────────────────────────────────────────────────────────────── */